import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import {
    TrendingUp,
    Users,
    ShoppingCart,
    MapPin,
    Package,
    CreditCard,
    Truck,
    Calendar,
    BarChart3,
    DollarSign,
    Zap,
    Filter,
    Eye,
    PieChart,
    Target,
    TrendingDown,
    Rocket,
    Clock,
    Award
} from 'lucide-react'
import type { StatisticsData, ProductDetails } from '../types/statistics'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { apiClient } from '../lib/apiClient'
import TelegramButton from '../components/TelegramButton'

const StatisticsPage: React.FC = () => {
    const { accessToken, user } = useAuthStore()
    const [isSticky, setIsSticky] = useState(false)
    const [dateFilter, setDateFilter] = useState('all')
    const [selectedImage, setSelectedImage] = useState<string | null>(null)

    // Scroll event listener for sticky behavior
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            setIsSticky(scrollTop > 200) // 200px scroll sonrası sticky ol
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])
    const [customDateFrom, setCustomDateFrom] = useState('')
    const [customDateTo, setCustomDateTo] = useState('')
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({})
    const [depletedSortOrder, setDepletedSortOrder] = useState<'asc' | 'desc'>('asc')
    const [depletedDateFrom, setDepletedDateFrom] = useState('')
    const [depletedDateTo, setDepletedDateTo] = useState('')
    const [velocityAgeFilter, setVelocityAgeFilter] = useState('all')
    const [velocityDateFrom, setVelocityDateFrom] = useState('')
    const [velocityDateTo, setVelocityDateTo] = useState('')
    const [depletedSkuFilter, setDepletedSkuFilter] = useState('')

    // Varsayılan tarih aralığını ayarla (bugün ve 7 gün öncesi)
    useEffect(() => {
        const today = new Date()
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(today.getDate() - 7)

        const formatDate = (date: Date) => {
            return date.toISOString().split('T')[0]
        }

        setDepletedDateTo(formatDate(today))
        setDepletedDateFrom(formatDate(sevenDaysAgo))
    }, [])

    // Ana istatistikler query'si
    const { data, isLoading, error, refetch } = useQuery<StatisticsData>({
        queryKey: ['statistics', dateFilter, customDateFrom, customDateTo],
        queryFn: async () => {
            let url = `/api/statistics?period=${dateFilter}`
            if (dateFilter === 'custom' && customDateFrom && customDateTo) {
                url += `&dateFrom=${customDateFrom}&dateTo=${customDateTo}`
            }

            const response = await apiClient.get(url)
            if (!response.ok) throw new Error('İstatistikler yüklenemedi')
            return response.json()
        },
        refetchInterval: 300000,
        enabled: !!accessToken,
    })

    // Ürün detay query'si
    const { data: productDetails } = useQuery<ProductDetails>({
        queryKey: ['product-details', selectedProduct, dateFilter, customDateFrom, customDateTo],
        queryFn: async () => {
            let url = `/api/statistics/product/${selectedProduct}?period=${dateFilter}`
            if (dateFilter === 'custom' && customDateFrom && customDateTo) {
                url += `&dateFrom=${customDateFrom}&dateTo=${customDateTo}`
            }

            const response = await apiClient.get(url)
            if (!response.ok) throw new Error('Ürün detayları yüklenemedi')
            return response.json()
        },
        enabled: !!accessToken && !!selectedProduct,
    })

    const handleDateFilterChange = (period: string) => {
        setDateFilter(period)
        if (period !== 'custom') {
            setCustomDateFrom('')
            setCustomDateTo('')
        }
    }

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }))
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    // Admin olmayan kullanıcılar için erişim engelleme
    if (!user?.roles.includes('admin')) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Erişim Engellendi!</h2>
                    <p className="text-gray-600 mb-4">Bu sayfaya erişim için admin yetkisi gereklidir.</p>
                    <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-500">
                            Mevcut rolleriniz: {user?.roles.join(', ') || 'Rol bulunamadı'}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Hata!</h2>
                    <p className="text-gray-600 mb-4">İstatistikler yüklenirken bir hata oluştu</p>
                    <button
                        onClick={() => refetch()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Tekrar Dene
                    </button>
                </div>
            </div>
        )
    }

    if (!data) return null

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
                <div className="bg-white shadow-lg border-b">
                    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">İstatistik Merkezi</h1>
                                <p className="text-gray-600 mt-2">Detaylı satış performansı ve analiz verileri</p>
                            </div>
                            <button
                                onClick={() => refetch()}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                            >
                                <BarChart3 className="w-4 h-4" />
                                Yenile
                            </button>
                        </div>

                        {/* Filtreleme Seçenekleri - Modal açıkken gizle */}
                        {!selectedImage && !selectedProduct && (
                            <div
                                className={`z-50 bg-white p-4 rounded-lg border shadow-lg border-gray-200 transition-all duration-300 ${isSticky ? 'fixed top-16 left-0 right-0 mx-4' : 'sticky top-16'
                                    }`}
                                style={{
                                    position: isSticky ? 'fixed' : 'sticky',
                                    top: isSticky ? '64px' : '64px',
                                    zIndex: 9999,
                                    backgroundColor: 'white',
                                    width: isSticky ? 'calc(100% - 2rem)' : '100%',
                                    marginTop: '0px'
                                }}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <Filter className="w-5 h-5 text-blue-600" />
                                    <h3 className="text-lg font-medium">Filtreleme Seçenekleri</h3>
                                </div>
                                <div className="flex flex-wrap gap-4 items-end">
                                    <div className="flex gap-2">
                                        {[
                                            { value: 'all', label: 'Tümü' },
                                            { value: 'daily', label: 'Günlük' },
                                            { value: 'weekly', label: 'Haftalık' },
                                            { value: 'monthly', label: 'Aylık' },
                                            { value: 'custom', label: 'Özel Tarih' }
                                        ].map(({ value, label }) => (
                                            <button
                                                key={value}
                                                onClick={() => handleDateFilterChange(value)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${dateFilter === value
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>

                                    {dateFilter === 'custom' && (
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="date"
                                                value={customDateFrom}
                                                onChange={(e) => setCustomDateFrom(e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm"
                                                placeholder="Başlangıç"
                                            />
                                            <span className="text-gray-500">-</span>
                                            <input
                                                type="date"
                                                value={customDateTo}
                                                onChange={(e) => setCustomDateTo(e.target.value)}
                                                className="px-3 py-2 border rounded-lg text-sm"
                                                placeholder="Bitiş"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
                    {/* Ana Metrikler */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-blue-100">Toplam Müşteri</p>
                                        <p className="text-3xl font-bold">{data.customerStats.totalCustomers}</p>
                                    </div>
                                    <Users className="w-12 h-12 text-blue-200" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-green-100">Toplam Sipariş</p>
                                        <p className="text-3xl font-bold">{data.customerStats.totalOrders}</p>
                                    </div>
                                    <ShoppingCart className="w-12 h-12 text-green-200" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-purple-100">Toplam Gelir</p>
                                        <p className="text-3xl font-bold">₺{Number(data.customerStats.totalRevenue || 0).toFixed(2)}</p>
                                    </div>
                                    <DollarSign className="w-12 h-12 text-purple-200" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-orange-100">Ortalama Sipariş</p>
                                        <p className="text-3xl font-bold">₺{Number(data.customerStats.avgOrderValue || 0).toFixed(2)}</p>
                                    </div>
                                    <TrendingUp className="w-12 h-12 text-orange-200" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* En Çok Satan Ürünler ve Bölgeler */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Package className="w-5 h-5 text-blue-600" />
                                        En Çok Satan Ürünler (Kargo Hariç)
                                    </div>
                                    <button
                                        onClick={() => toggleSection('topProducts')}
                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                        title={expandedSections.topProducts ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.topProducts ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.topProducts.slice(0, 50).map((product, index) => (
                                        <div key={product.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center">
                                                    {index + 1}
                                                </Badge>
                                                <div>
                                                    <p className="font-medium text-sm">{product.productName}</p>
                                                    <p className="text-xs text-gray-500">{product.code}</p>
                                                </div>
                                            </div>
                                            <div className="text-right flex items-center gap-2">
                                                <div>
                                                    <p className="font-semibold text-sm">{product.totalQuantity} adet</p>
                                                    <p className="text-xs text-green-600">₺{Number(product.totalRevenue).toFixed(2)}</p>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    <button
                                                        onClick={() => setSelectedProduct(product.code)}
                                                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                                        title="Detaylı İstatistikler"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <TelegramButton
                                                        productId={product.code}
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-green-600 border-green-200 hover:bg-green-50"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-green-600" />
                                        En Çok Satış Yapılan Bölgeler
                                    </div>
                                    <button
                                        onClick={() => toggleSection('topRegions')}
                                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                        title={expandedSections.topRegions ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.topRegions ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.topRegions.slice(0, 50).map((region, index) => (
                                        <div key={`${region.deliveryCity}-${region.deliveryDistrict}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center">
                                                    {index + 1}
                                                </Badge>
                                                <div>
                                                    <p className="font-medium text-sm">{region.deliveryCity}</p>
                                                    <p className="text-xs text-gray-500">{region.deliveryDistrict}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm">{region.orderCount} sipariş</p>
                                                <p className="text-xs text-green-600">₺{Number(region.totalRevenue).toFixed(2)}</p>
                                                <p className="text-xs text-blue-600">{region.uniqueCustomers} müşteri</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* SKU Bazlı Satışlar ve Kategori İstatistikleri */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Target className="w-5 h-5 text-indigo-600" />
                                        Stok Kodu Bazlı Satışlar
                                    </div>
                                    <button
                                        onClick={() => toggleSection('skuBasedSales')}
                                        className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                                        title={expandedSections.skuBasedSales ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.skuBasedSales ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.skuBasedSales && data.skuBasedSales.slice(0, 50).map((sku, index) => (
                                        <div key={sku.skuPrefix} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center">
                                                    {index + 1}
                                                </Badge>
                                                <div>
                                                    <p className="font-medium text-sm">{sku.skuPrefix} kodlu ürünler</p>
                                                    <p className="text-xs text-gray-500">{sku.productCount} farklı ürün</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm">{sku.totalQuantity} adet</p>
                                                <p className="text-xs text-green-600">₺{Number(sku.totalRevenue).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <PieChart className="w-5 h-5 text-purple-600" />
                                        Kategori Bazlı Satışlar
                                    </div>
                                    <button
                                        onClick={() => toggleSection('categoryStats')}
                                        className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                                        title={expandedSections.categoryStats ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.categoryStats ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.categoryStats && data.categoryStats.slice(0, 50).map((category, index) => (
                                        <div key={category.categoryName} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center">
                                                    {index + 1}
                                                </Badge>
                                                <div>
                                                    <p className="font-medium text-sm">{category.categoryName}</p>
                                                    <p className="text-xs text-gray-500">{category.productCount} ürün</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm">{category.totalQuantity} adet</p>
                                                <p className="text-xs text-green-600">₺{Number(category.totalRevenue).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Aylık Performans ve Haftalık Performans */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-orange-600" />
                                        Aylık Ürün Performansı
                                    </div>
                                    <button
                                        onClick={() => toggleSection('monthlyStats')}
                                        className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                                        title={expandedSections.monthlyStats ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.monthlyStats ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.monthlyStats && data.monthlyStats.slice(0, 50).map((month, index) => (
                                        <div key={`${month.monthStart}-${month.code}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div>
                                                <p className="font-medium text-sm">{month.productName}</p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(month.monthStart).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })} • {month.code}
                                                </p>
                                            </div>
                                            <div className="text-right flex flex-col items-end gap-2">
                                                <div>
                                                    <p className="font-semibold text-sm">{month.totalQuantity} adet</p>
                                                    <p className="text-xs text-green-600">₺{Number(month.totalRevenue).toFixed(2)}</p>
                                                </div>
                                                <TelegramButton
                                                    productId={month.code}
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-green-600 border-green-200 hover:bg-green-50"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                    </div>


                    {/* Sipariş ve Ciro İstatistikleri */}
                    {data.orderRevenueStats && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                            <Card className="shadow-xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <BarChart3 className="w-5 h-5 text-teal-600" />
                                        Sipariş Büyüklüğüne Göre Ciro
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {data.orderRevenueStats.orderSizeBreakdown.map((size, index) => (
                                            <div key={size.orderSizeRange} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div>
                                                    <p className="font-medium text-sm">{size.orderSizeRange}</p>
                                                    <p className="text-xs text-gray-500">{size.orderCount} sipariş</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-sm">₺{Number(size.totalRevenue).toFixed(2)}</p>
                                                    <p className="text-xs text-blue-600">₺{Number(size.avgOrderValue).toFixed(2)} ort.</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="shadow-xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                                        Genel Sipariş İstatistikleri
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.orderRevenueStats.general && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-3 bg-blue-50 rounded-lg">
                                                    <p className="text-xs text-blue-600 mb-1">Toplam Sipariş</p>
                                                    <p className="text-lg font-bold text-blue-800">{data.orderRevenueStats.general.totalOrders}</p>
                                                </div>
                                                <div className="p-3 bg-green-50 rounded-lg">
                                                    <p className="text-xs text-green-600 mb-1">Toplam Ciro</p>
                                                    <p className="text-lg font-bold text-green-800">₺{Number(data.orderRevenueStats.general.totalRevenue).toFixed(2)}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-3 bg-orange-50 rounded-lg">
                                                    <p className="text-xs text-orange-600 mb-1">Ortalama Sipariş</p>
                                                    <p className="text-lg font-bold text-orange-800">₺{Number(data.orderRevenueStats.general.avgOrderValue).toFixed(2)}</p>
                                                </div>
                                                <div className="p-3 bg-purple-50 rounded-lg">
                                                    <p className="text-xs text-purple-600 mb-1">En Yüksek Sipariş</p>
                                                    <p className="text-lg font-bold text-purple-800">₺{Number(data.orderRevenueStats.general.maxOrderValue).toFixed(2)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Ödeme Yöntemleri ve Kargo Firmaları */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-pink-600" />
                                        Ödeme Yöntemleri
                                    </div>
                                    <button
                                        onClick={() => toggleSection('paymentStats')}
                                        className="p-2 text-pink-600 hover:bg-pink-100 rounded-lg transition-colors"
                                        title={expandedSections.paymentStats ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.paymentStats ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.paymentStats.slice(0, 50).map((payment) => (
                                        <div key={payment.paymentMethod} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div>
                                                <p className="font-medium text-sm">{payment.paymentMethod}</p>
                                                <p className="text-xs text-gray-500">{payment.orderCount} sipariş</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm">₺{Number(payment.totalRevenue).toFixed(2)}</p>
                                                <p className="text-xs text-blue-600">₺{Number(payment.avgOrderValue).toFixed(2)} ort.</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Truck className="w-5 h-5 text-teal-600" />
                                        Kargo Firması Performansı
                                    </div>
                                    <button
                                        onClick={() => toggleSection('shippingStats')}
                                        className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
                                        title={expandedSections.shippingStats ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.shippingStats ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                    {data.shippingStats.slice(0, 50).map((shipping) => (
                                        <div key={shipping.shippingCompany} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div>
                                                <p className="font-medium text-sm">{shipping.shippingCompany}</p>
                                                <p className="text-xs text-gray-500">{shipping.orderCount} sipariş</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm">₺{Number(shipping.totalRevenue).toFixed(2)}</p>
                                                <p className="text-xs text-blue-600">₺{Number(shipping.avgOrderValue).toFixed(2)} ort.</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Gelişmiş Velocity Analizi */}
                    {data.velocityRankings && data.velocityRankings.length > 0 && (
                        <Card className="shadow-xl mb-8">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Rocket className="w-5 h-5 text-purple-600" />
                                        Gelişmiş Satış Hızı (Velocity) Sıralaması
                                    </div>
                                    <button
                                        onClick={() => toggleSection('velocityRankings')}
                                        className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                                        title={expandedSections.velocityRankings ? "Daralt" : "Genişlet"}
                                    >
                                        {expandedSections.velocityRankings ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        )}
                                    </button>
                                </CardTitle>
                                <div className="flex flex-col gap-3">
                                    <p className="text-sm text-gray-600">
                                        XML eklenme tarihi bazlı normalize edilmiş satış performansı (α=5, β=7)
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Yaş Kategorisi:</span>
                                            <select
                                                value={velocityAgeFilter}
                                                onChange={(e) => setVelocityAgeFilter(e.target.value)}
                                                className="px-2 py-1 text-sm border rounded-lg bg-white"
                                            >
                                                <option value="all">Tümü</option>
                                                <option value="Yeni">Yeni</option>
                                                <option value="Genç">Genç</option>
                                                <option value="Olgun">Olgun</option>
                                                <option value="Eski">Eski</option>
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Tarih Aralığı:</span>
                                            <input
                                                type="date"
                                                value={velocityDateFrom}
                                                onChange={(e) => setVelocityDateFrom(e.target.value)}
                                                className="px-2 py-1 text-sm border rounded-lg"
                                                placeholder="Başlangıç"
                                            />
                                            <span className="text-gray-500">-</span>
                                            <input
                                                type="date"
                                                value={velocityDateTo}
                                                onChange={(e) => setVelocityDateTo(e.target.value)}
                                                className="px-2 py-1 text-sm border rounded-lg"
                                                placeholder="Bitiş"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Top 10 Velocity */}
                                    <div>
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <Award className="w-4 h-4 text-gold-500" />
                                            En Yüksek Velocity (Top 50)
                                        </h4>
                                        <div className="space-y-2">
                                            {data.velocityRankings
                                                .filter(product => {
                                                    // Yaş kategorisi filtresi
                                                    if (velocityAgeFilter !== 'all' && product.ageCategory !== velocityAgeFilter) {
                                                        return false
                                                    }

                                                    // Tarih aralığı filtresi (dateAdd bazında)
                                                    if (velocityDateFrom || velocityDateTo) {
                                                        const productDate = new Date(product.dateAdd)
                                                        const fromDate = velocityDateFrom ? new Date(velocityDateFrom) : null
                                                        const toDate = velocityDateTo ? new Date(velocityDateTo) : null

                                                        if (fromDate && productDate < fromDate) return false
                                                        if (toDate && productDate > toDate) return false
                                                    }

                                                    return true
                                                })
                                                .slice(0, 50)
                                                .map((product) => (
                                                    <div key={product.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                                                        <div className="flex items-center gap-3">
                                                            <Badge variant="secondary" className="w-6 h-6 flex items-center justify-center text-xs">
                                                                {product.velocityRank}
                                                            </Badge>
                                                            <div>
                                                                <p className="font-medium text-sm text-gray-800">{product.name}</p>
                                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                    <span>{product.sku}</span>
                                                                    <Badge
                                                                        variant="outline"
                                                                        className={`text-xs ${product.ageCategory === 'Yeni' ? 'text-green-600 border-green-300' :
                                                                            product.ageCategory === 'Genç' ? 'text-blue-600 border-blue-300' :
                                                                                product.ageCategory === 'Olgun' ? 'text-orange-600 border-orange-300' :
                                                                                    'text-gray-600 border-gray-300'
                                                                            }`}
                                                                    >
                                                                        {product.ageCategory}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex flex-col items-end gap-2">
                                                            <div>
                                                                <p className="font-bold text-sm text-purple-600">
                                                                    {product.finalVelocityScore.toFixed(3)}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {product.totalSales} adet • {product.ageDays} gün
                                                                </p>
                                                                <div className="flex items-center gap-1 text-xs">
                                                                    <span className="text-blue-600">V:{product.smoothedVelocity.toFixed(2)}</span>
                                                                    <span className="text-green-600">M:{product.momentum14.toFixed(2)}</span>
                                                                </div>
                                                            </div>
                                                            <TelegramButton
                                                                productId={product.sku}
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-green-600 border-green-200 hover:bg-green-50"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>

                                    {/* Yeni Ürünler (Momentum Odaklı) */}
                                    <div>
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-green-500" />
                                            Yüksek Momentum (Yeni Trendler)
                                        </h4>
                                        <div className="space-y-2">
                                            {data.velocityRankings
                                                .filter(p => p.recent14Sales > 0)
                                                .filter(product => {
                                                    // Yaş kategorisi filtresi
                                                    if (velocityAgeFilter !== 'all' && product.ageCategory !== velocityAgeFilter) {
                                                        return false
                                                    }

                                                    // Tarih aralığı filtresi (dateAdd bazında)
                                                    if (velocityDateFrom || velocityDateTo) {
                                                        const productDate = new Date(product.dateAdd)
                                                        const fromDate = velocityDateFrom ? new Date(velocityDateFrom) : null
                                                        const toDate = velocityDateTo ? new Date(velocityDateTo) : null

                                                        if (fromDate && productDate < fromDate) return false
                                                        if (toDate && productDate > toDate) return false
                                                    }

                                                    return true
                                                })
                                                .sort((a, b) => b.momentum14 - a.momentum14)
                                                .slice(0, 50)
                                                .map((product, index) => (
                                                    <div key={product.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg">
                                                        <div className="flex items-center gap-3">
                                                            <Badge variant="secondary" className="w-6 h-6 flex items-center justify-center text-xs">
                                                                {index + 1}
                                                            </Badge>
                                                            <div>
                                                                <p className="font-medium text-sm text-gray-800">{product.name}</p>
                                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                    <span>{product.sku}</span>
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-xs text-green-600 border-green-300"
                                                                    >
                                                                        Son 14 gün: {product.recent14Sales} adet
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex flex-col items-end gap-2">
                                                            <div>
                                                                <p className="font-bold text-sm text-green-600">
                                                                    {product.momentum14.toFixed(3)}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    momentum/gün
                                                                </p>
                                                                <p className="text-xs text-purple-600">
                                                                    Final: {product.finalVelocityScore.toFixed(3)}
                                                                </p>
                                                            </div>
                                                            <TelegramButton
                                                                productId={product.sku}
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-green-600 border-green-200 hover:bg-green-50"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Velocity Algoritması Açıklaması */}
                                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                    <h5 className="font-semibold text-sm mb-2">📊 Algoritma Detayları:</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600">
                                        <div>
                                            <p><strong>Smoothed Velocity:</strong> (total_sales + 5) / (age_days + 7)</p>
                                            <p><strong>Momentum:</strong> recent_14_sales / min(14, age_days)</p>
                                        </div>
                                        <div>
                                            <p><strong>Final Score:</strong> 70% velocity + 30% momentum</p>
                                            <p><strong>Minimum Eşik:</strong> En az 3 adet satış gerekli</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}



                    {/* Ürün Detay Modal */}
                    {selectedProduct && productDetails && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                                <div className="p-6 border-b">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-2xl font-bold">Ürün Detaylı İstatistikleri</h2>
                                        <button
                                            onClick={() => setSelectedProduct(null)}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                                <div className="p-6">
                                    {false ? (
                                        <div>Product details loaded</div>
                                    ) : (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div>
                                                    <h3 className="text-lg font-semibold mb-3">Ürün Bilgileri</h3>
                                                    <div className="space-y-2">
                                                        <p><span className="font-medium">SKU:</span> {productDetails.product.code}</p>
                                                        <p><span className="font-medium">Ürün Adı:</span> {productDetails.product.productName}</p>
                                                        <p><span className="font-medium">Toplam Satılan:</span> {productDetails.product.totalQuantity} adet</p>
                                                        <p><span className="font-medium">Toplam Gelir:</span> ₺{Number(productDetails.product.totalRevenue).toFixed(2)}</p>
                                                        <p><span className="font-medium">Sipariş Sayısı:</span> {productDetails.product.orderCount}</p>
                                                        <p><span className="font-medium">Benzersiz Müşteri:</span> {productDetails.product.uniqueCustomers}</p>
                                                        <p><span className="font-medium">Ortalama Fiyat:</span> ₺{Number(productDetails.product.avgPrice).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold mb-3">Satış Tarihleri</h3>
                                                    <div className="space-y-2">
                                                        <p><span className="font-medium">İlk Satış:</span> {new Date(productDetails.product.firstSaleDate).toLocaleDateString('tr-TR')}</p>
                                                        <p><span className="font-medium">Son Satış:</span> {new Date(productDetails.product.lastSaleDate).toLocaleDateString('tr-TR')}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold mb-3">Ürün Görseli</h3>
                                                    <div className="flex justify-center">
                                                        {productDetails.product.image1 || productDetails.product.image2 || productDetails.product.image3 ? (
                                                            <div
                                                                className="w-48 h-48 rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-200 border-2 border-gray-200"
                                                                onClick={() => setSelectedImage(productDetails.product.image1 || productDetails.product.image2 || productDetails.product.image3 || null)}
                                                            >
                                                                <img
                                                                    src={productDetails.product.image1 || productDetails.product.image2 || productDetails.product.image3 || undefined}
                                                                    alt={productDetails.product.productName}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.style.display = 'none';
                                                                        target.nextElementSibling?.classList.remove('hidden');
                                                                    }}
                                                                />
                                                                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-600 font-bold text-lg hidden">
                                                                    <div className="text-center">
                                                                        <div className="text-4xl mb-2">📦</div>
                                                                        <div className="text-sm">Görsel Yok</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="w-48 h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center text-gray-600">
                                                                <div className="text-center">
                                                                    <div className="text-4xl mb-2">📦</div>
                                                                    <div className="text-sm">Görsel Yok</div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className="text-lg font-semibold mb-3">Aylık Satış Trendi</h3>
                                                <div className="space-y-2">
                                                    {productDetails.monthlyTrend.map((trend) => (
                                                        <div key={trend.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                            <p className="font-medium">
                                                                {new Date(trend.month).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}
                                                            </p>
                                                            <div className="text-right">
                                                                <p className="font-semibold">{trend.quantity} adet</p>
                                                                <p className="text-sm text-green-600">₺{Number(trend.revenue).toFixed(2)}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Ürün Görsel Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative max-w-4xl max-h-full">
                        <img
                            src={selectedImage || ''}
                            alt="Ürün Görseli"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 text-gray-600 hover:text-gray-800 transition-all duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default StatisticsPage